import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSalesInvoiceContext } from "../../../context/SalesInvoiceContext";
import { useReceiptVoucherContext } from "../../../context/ReceiptVoucherContext";
import { formatDate } from "../../../utils/dateFormatter";
import { BranchContactHeader } from "../../../components/branchContact/BranchContact";
import "./salesInvoiceDetails.css";

const SalesInvoiceDetails = () => {
  const { id } = useParams();
  const { getSalesInvoice, cancelSalesInvoice } = useSalesInvoiceContext();
  const { getInvoiceVouchers } = useReceiptVoucherContext();
  const [invoice, setInvoice] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoiceDetails = async () => {
    try {
      const response = await getSalesInvoice(id);
      if (response.status === "success") {
        setInvoice(response.invoice);
      }

      // Fetch vouchers for this invoice
      const vouchersResponse = await getInvoiceVouchers(id);
      console.log(vouchersResponse);

      if (vouchersResponse.status === "success") {
        console.log("Vouchers data:", vouchersResponse.vouchers);
        setVouchers(vouchersResponse.vouchers);
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleCancelInvoice = async () => {
    if (
      !window.confirm(
        "هل أنت متأكد من إلغاء هذه الفاتورة؟\nسيتم إرجاع الكميات وتحديث الرصيد.",
      )
    ) {
      return;
    }

    try {
      const response = await cancelSalesInvoice(id);
      if (response.status === "success") {
        alert("✅ " + response.message);
        fetchInvoiceDetails(); // إعادة تحميل تفاصيل الفاتورة
      }
    } catch (err) {
      alert("❌ " + (err.response?.data?.message || "حدث خطأ أثناء الإلغاء"));
    }
  };

  if (loading) {
    return (
      <div className="invoice-details-page">
        <div className="loading-container">
          <p>جارٍ تحميل تفاصيل الفاتورة...</p>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-details-page">
        <div className="error-container">
          <p>لم يتم العثور على الفاتورة</p>
          <Link to="/sales-invoices">العودة للفواتير</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-details-page">
      <div className="invoice-container">
        {/* Background Watermark */}
        <div className="invoice-watermark">
          <img src={`/assets/bg_bill.png`} alt="" />
        </div>

        {/* Action Buttons */}
        <div className="action-buttons no-print">
          <Link to="/sales-invoices" className="back-btn">
            ← العودة للفواتير
          </Link>
          <button className="print-btn" onClick={handlePrint}>
            🖨️ طباعة الفاتورة
          </button>
          {invoice.is_canceled != 1 && (
            <button
              className="cancel-btn"
              onClick={handleCancelInvoice}
              style={{
                backgroundColor: "#ef4444",
                color: "white",
                padding: "10px 20px",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "16px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#dc2626";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#ef4444";
              }}
            >
              ❌ إلغاء الفاتورة
            </button>
          )}
          {invoice.is_canceled == 1 && (
            <div
              style={{
                padding: "12px 24px",
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                borderRadius: "8px",
                fontWeight: "700",
                fontSize: "18px",
                border: "2px solid #dc2626",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              ⚠️ هذه الفاتورة ملغية
            </div>
          )}
        </div>

        {/* Invoice Header */}
        <div className="invoice-header">
          <BranchContactHeader branch={invoice.branch} />
          <div className="invoice-number">رقم الفاتورة: #{invoice.id}</div>
          <div className="invoice-date">
            التاريخ: {formatDate(invoice.date)}
          </div>
        </div>

        {/* Counterparty Info */}
        <div className="info-section">
          <h2>
            معلومات {invoice.party_type === "supplier" ? "المورد" : "الزبون"}
          </h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">الاسم:</span>
              <span className="value">
                <a
                  href={`/${invoice.party_type}/${
                    invoice.party_type === "supplier"
                      ? invoice.supplier_id
                      : invoice.customer_id
                  }`}
                >
                  {invoice.party_name}
                </a>
              </span>
            </div>
            <div className="info-item">
              <span className="label">الهاتف:</span>
              <span className="value">{invoice.party_phone}</span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="items-section">
          <h2>الأصناف</h2>
          <table className="items-table">
            <thead>
              <tr>
                <th>اسم الصنف</th>
                <th>الكمية</th>
                <th>سعر الوحدة</th>
                <th>المجموع</th>
                <th className="no-print">صافي الربح</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => {
                const purchasePrice = parseFloat(item.purchase_price || 0);
                const salePrice = parseFloat(item.unit_price);
                const quantity = parseFloat(item.quantity);
                const profitPerUnit = salePrice - purchasePrice;
                const totalProfit = profitPerUnit * quantity;

                return (
                  <tr key={item.id}>
                    <td>
                      {item.product_name}
                      {item.description && ` - ${item.description}`}
                    </td>
                    <td>{item.quantity}</td>
                    <td>{salePrice.toFixed(2)} ₪</td>
                    <td>{parseFloat(item.subtotal).toFixed(2)} ₪</td>
                    <td
                      className="no-print"
                      style={{
                        color: totalProfit >= 0 ? "#10b981" : "#ef4444",
                        fontWeight: "600",
                      }}
                    >
                      {totalProfit.toFixed(2)} ₪
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3" className="total-label">
                  المجموع الإجمالي:
                </td>
                <td className="total-amount">
                  {parseFloat(invoice.total_amount).toFixed(2)} ₪
                </td>
                <td className="no-print"></td>
              </tr>
              <tr className="no-print">
                <td colSpan="3" className="total-label">
                  صافي الربح:
                </td>
                <td
                  colSpan="2"
                  className="total-amount"
                  style={{
                    color:
                      invoice.items.reduce((sum, item) => {
                        const profitPerUnit =
                          parseFloat(item.unit_price) -
                          parseFloat(item.purchase_price || 0);
                        return sum + profitPerUnit * parseFloat(item.quantity);
                      }, 0) >= 0
                        ? "#10b981"
                        : "#ef4444",
                  }}
                >
                  {invoice.items
                    .reduce((sum, item) => {
                      const profitPerUnit =
                        parseFloat(item.unit_price) -
                        parseFloat(item.purchase_price || 0);
                      return sum + profitPerUnit * parseFloat(item.quantity);
                    }, 0)
                    .toFixed(2)}{" "}
                  ₪
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="invoice-notes">
            <h2>ملاحظات</h2>
            <p>{invoice.notes}</p>
          </div>
        )}

        {/* Payment Summary */}
        <div className="payment-summary">
          <h2>ملخص الدفعات</h2>
          <div className="payment-info">
            <div className="payment-row">
              <span className="payment-label">المبلغ الإجمالي:</span>
              <span className="payment-value invoice-total">
                {parseFloat(invoice.total_amount).toFixed(2)} ₪
              </span>
            </div>
            <div className="payment-row">
              <span className="payment-label">المبلغ المدفوع:</span>
              <span className="payment-value invoice-paid">
                {vouchers
                  .reduce((sum, v) => sum + (parseFloat(v.amount) || 0), 0)
                  .toFixed(2)}{" "}
                ₪
              </span>
            </div>
            <div className="payment-row remaining">
              <span className="payment-label">المبلغ المتبقي:</span>
              <span className="payment-value invoice-remaining">
                {(
                  parseFloat(invoice.total_amount) -
                  vouchers.reduce(
                    (sum, v) => sum + (parseFloat(v.amount) || 0),
                    0,
                  )
                ).toFixed(2)}{" "}
                ₪
              </span>
            </div>
          </div>
        </div>

        {/* Receipt Vouchers */}
        {vouchers.length > 0 && (
          <div className="vouchers-section">
            <h2>سندات القبض</h2>
            <table className="vouchers-table">
              <thead>
                <tr>
                  <th>المبلغ</th>
                  <th>نوع الدفع</th>
                  <th>التاريخ</th>
                  <th>الملاحظات</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.id} style={{ cursor: "pointer" }}>
                    <td>
                      <Link
                        to={`/receipt-voucher/${voucher.id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {parseFloat(voucher.amount).toFixed(2)} ₪
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/receipt-voucher/${voucher.id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        <span
                          className={`payment-badge ${
                            voucher.payment_type === "cash"
                              ? "badge-cash"
                              : "badge-check"
                          }`}
                        >
                          {voucher.payment_type === "كاش" ? "كاش" : "شيك"}
                        </span>
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/receipt-voucher/${voucher.id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {formatDate(voucher.created_at)}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/receipt-voucher/${voucher.id}`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {voucher.notes || "-"}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesInvoiceDetails;
